<template>
  <div
    class="oct-popover"
    :id="id"
    :class="{ 'oct-popover--open': isOpen }"
    :style="{
      top: $data.$_octPopover_position.top + 'px',
      left: $data.$_octPopover_position.left + 'px'
    }"
  >
    <div class="oct-popover__content">
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * @typedef {Object} useOctPopoverComposable
 * @property {Function} open - Opens the popover for the specified element.
 * @property {Function} close - Closes the popover with the specified ID.
 */

/**
 * The controller for the OctPopover component.
 */
class OctPopoverController {
  constructor () {
    /**
     * The collection of popovers.
     * @type {Object}
     */
    this.popovers = {}
  }

  /**
   * Adds a popover to the collection.
   *
   * @param {Object} popover - The popover object to be added.
   * @param {string} id - The unique identifier for the popover.
   */
  add (popover, id) {
    // If id is not provided or empty, warn the user
    if (!id)
      return console.warn('OctPopover: id is required', popover)

    // If the popover is not provided, warn the user
    if (!popover)
      return console.warn('OctPopover: popover is required', id)

    this.popovers[id] = popover
  }

  /**
   * Removes the specified popover from the collection.
   * 
   * @param {Object} popover - The popover object to be removed.
   */
  remove (popover) {
    delete this.popovers[popover.id]
  }

  /**
   * Opens the popover for the specified element.
   *
   * @param {string} id - The identifier for the popover.
   * @param {HTMLElement} el - The HTML element to which the popover is attached.
   */
  open (id, el) {
    this.popovers[id] && this.popovers[id].open(el)
  }

  /**
   * Closes the popover with the specified ID.
   *
   * @param {string} id - The ID of the popover to close.
   */
  close (id) {
    this.popovers[id].close()
  }
}

const octPopoverControllerInstance = new OctPopoverController()

/**
 * A composable function that provides functionality for the OctPopover component.
 * 
 * @returns {useOctPopoverComposable} The OctPopover composable object.
 */
export const useOctPopover = () => {
  return {
    open: (id, el) => octPopoverControllerInstance.open(id, el),
    close: (id) => octPopoverControllerInstance.close(id)
  }
}

export default {
  name: 'OctPopover',
  /**
   * The props for the OctPopover component.
   * @property {string} id - The unique identifier for the popover.
   */
  props: {
    id: {
      type: String,
      default: ''
    }
  },
  /**
   * The data object for the OctPopover component.
   * @property {boolean} isOpen - Indicates whether the popover is open.
   * @property {Object} $_octPopover_position - The position of the popover.
   * @property {number} $_octPopover_position.top - The top position of the popover.
   * @property {number} $_octPopover_position.left - The left position of the popover.
   */
  data () {
    return {
      isOpen: false,
      $_octPopover_position: {
        top: 0,
        left: 0
      }
    }
  },
  methods: {
    /**
     * Opens the popover for the specified element.
     *
     * @param {HTMLElement} el - The HTML element to which the popover is attached.
     */
    open (el) {
      // Set the open position
      this.$data.$_octPopover_position = this.$_octPopover_getRelativePosition(el)

      // Open the popover
      this.isOpen = true
    },
    /**
     * Closes the popover.
     */
    close () {
      // Close the popover
      this.isOpen = false

      // Reset the position
      this.$data.$_octPopover_position = {
        top: 0,
        left: 0
      }
    },
    /**
     * Returns the relative position of the target element.
     *
     * @param {HTMLElement} el - The target element.
     * @returns {Object} The relative position of the target element.
     */
    $_octPopover_getRelativePosition (el) {
      const rectComponent = this.$el.getBoundingClientRect()
      const rectTraget = el.getBoundingClientRect()

      // Check if the popover is on the right side of the target element
      const opposite = rectTraget.left + rectTraget.width + rectComponent.width > window.innerWidth

      // Return the relative position of the right top corner of the target element
      return opposite ? {
        top: rectTraget.top - rectComponent.top - rectComponent.height,
        left: rectTraget.left + rectTraget.width - rectComponent.left - rectComponent.width
      } : {
        top: rectTraget.top - rectComponent.top,
        left: rectTraget.left - rectComponent.left + rectTraget.width
      }
    }
  },
  mounted () {
    // Add the popover to the controller
    octPopoverControllerInstance.add(this, this.id)
  },
  beforeDestroy () {
    // Remove the popover from the controller
    octPopoverControllerInstance.remove(this)
  }
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-popover {
  font-family: $font-family-sans-serif-ja;
  text-align: left;
  text-transform: initial;
  white-space: initial;
  position: absolute;
  visibility: hidden;
  padding: 0 0 oct-rem(8) oct-rem(8);
  cursor: default;

  &--open {
    visibility: visible;
  }
}

.oct-popover__content {
  font-size: oct-rem(15);
  line-height: (24/15);
  width: oct-rem(320);
  padding: oct-rem(12);
  background-color: #fff;
  border: 1px solid $oct-theme--secondary-border;
  border-radius: oct-rem(8);
}
</style>
